import React, { useState } from 'react';
import { CheckIcon } from 'lucide-react';
import { includedPerLight, includedPerLightEs, pricingConfig, remotesLabel, warrantyConfig } from '../../data/pricing';
import { useLanguage } from '../../contexts/LanguageContext';
import { useFunnel } from '../../contexts/FunnelContext';
import { formatMoney, priceIndividual } from '../../utils/pricing';
import { buttonClasses } from '../../utils/buttonStyles';
import { QuantityStepper } from '../ui/QuantityStepper';
import { WarrantyTag } from '../ui/WarrantyTag';
import { ProductAssetSlot } from '../product/ProductAssetSlot';
import { ProductRating } from '../reviews/ProductRating';
const QUICK_PICKS = [1, 2, 3, 4, 6, 8, 10, 12, 15, 20];
export function BuildYourOwn({
  initialQty = 1
}: {
  initialQty?: number;
}) {
  const {
    t,
    lang
  } = useLanguage();
  const {
    startIndividual
  } = useFunnel();
  const [qty, setQty] = useState(initialQty);
  const p = priceIndividual(qty);
  const m = (n: number) => formatMoney(n, lang);
  const included = lang === 'es' ? includedPerLightEs : includedPerLight;
  return <div className="grid gap-10 lg:grid-cols-[1.15fr_1fr] lg:gap-16" data-id="element-759" data-mp="2xavyi5qunnm0">
      <div data-id="element-760" data-mp="2x92p4iouqpab">
        <ProductAssetSlot view="front" tone="studio" className="aspect-[4/3] w-full" data-id="element-761" data-mp="2x8t0ozjggq6q" />
        <div className="mt-10" data-id="element-762" data-mp="2ifzkfbwd0610">
          <label className="block font-display text-lg font-semibold uppercase tracking-[0.08em]" htmlFor="byo-qty" data-id="element-763" data-mp="2ncq0w0c2t3my">
            How many M1 PRO lights?
          </label>
          <div className="mt-4" id="byo-qty" data-id="element-764" data-mp="3b4woob3qk2ar">
            <QuantityStepper value={qty} onChange={setQty} min={1} max={pricingConfig.maxIndividualQty} label="number of lights" size="lg" data-id="element-765" data-mp="3g1wy8vzsnn6m" />
          </div>
          <div className="mt-5 flex flex-wrap gap-2" role="group" aria-label="Quick quantity" data-id="element-766" data-mp="369hdjqx44ki1">
            {QUICK_PICKS.map(n => <button key={n} type="button" aria-pressed={qty === n} onClick={() => setQty(n)} className={`h-10 min-w-[48px] rounded-md border px-3 text-sm font-semibold transition-colors duration-150 ${qty === n ? 'border-marine bg-marine text-white' : 'mf-hairline text-titanium hover:text-white'}`} data-id="element-767" data-mp="2na8j0x7l6v7">
                {n}
              </button>)}
          </div>
        </div>

        <p className="mt-8 text-sm text-titanium" data-id="element-768" data-mp="3pqarsy6vga02">
          Each M1 PRO is {m(pricingConfig.unitPrice)}. Lighting 2, 4, 6, 8, 12 or 24? A complete system costs less than the same
          number of individual lights.
        </p>
      </div>

      <aside aria-label="Your system summary" className="lg:sticky lg:top-32 lg:self-start" data-id="element-769" data-mp="3fx3g6vljfxrj">
        <div className="rounded-2xl border mf-hairline bg-navy p-6 md:p-8" data-id="element-770" data-mp="3bvrqkq2nzcjc">
          <ProductRating data-id="element-771" data-mp="36y8cpr9vp1rx" />
          <h3 className="mt-3 font-display text-2xl font-semibold uppercase tracking-[0.04em]" data-id="element-772" data-mp="370m1xjhvnjrk">Your system</h3>
          <dl className="mt-6 space-y-3 text-sm" data-id="element-773" data-mp="1hhmhdham8p4u">
            <Row label="Selected quantity" value={`${qty} ${qty === 1 ? t('common.light') : t('common.lights')}`} data-id="element-774" data-mp="1hguoie8el5i9" />
            <Row label="Unit price" value={m(p.unit)} data-id="element-775" data-mp="1mew5mtm9qur3" />
          </dl>
          <div className="mt-6 flex items-baseline justify-between border-t mf-hairline pt-5" data-id="element-776" data-mp="1rgv9e4lx7tok">
            <span className="text-titanium" data-id="element-777" data-mp="1vgdrytq9rso8">{t('common.total')}</span>
            <span className="font-display text-5xl font-semibold" data-id="element-778" data-mp="yrel9j5c2yy5">{m(p.total)}</span>
          </div>

          <div className="mt-6 space-y-3 border-t mf-hairline pt-5 text-sm text-titanium" data-id="element-779" data-mp="13pzirat7b4iw">
            <WarrantyTag tier={warrantyConfig.individual} withShipping data-id="element-780" data-mp="17rui49rkn7u1" />
            <ul className="space-y-2" data-id="element-781" data-mp="17pq6f5nfl4j7">
              {included.map(i => <li key={i} className="flex gap-2" data-id="element-782" data-mp="1ck46vmgya1uq">
                  <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-marine" aria-hidden data-id="element-783" data-mp="g0wqza4urvao" />
                  {qty} × {i}
                </li>)}
              <li className="flex gap-2" data-id="element-784" data-mp="k0fg6i3pisjb">
                <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-marine" aria-hidden data-id="element-785" data-mp="xdi5uoo3yf9" />
                {remotesLabel(qty, lang)}
              </li>
            </ul>
          </div>

          <button type="button" onClick={() => startIndividual(qty)} className={`${buttonClasses('primary', 'lg')} mt-8 w-full`} data-id="element-786" data-mp="angartrvnad2">
            {t('common.addToCart')} — {m(p.total)}
          </button>
        </div>
      </aside>
    </div>;
}
function Row({
  label,
  value
}: {
  label: string;
  value: string;
}) {
  return <div className="flex justify-between gap-4" data-id="element-787" data-mp="eos4aadvgfqj">
      <dt className="text-titanium" data-id="element-788" data-mp="tmu9ep0jx99w">{label}</dt>
      <dd className="text-right text-white" data-id="element-789" data-mp="tlj6erexk5ju">{value}</dd>
    </div>;
}